import { useNavigate } from "react-router-dom";
import { Bell, CalendarClock, HardHat, CheckCheck, ClipboardCheck, Wallet, TrendingUp } from "lucide-react";
import { api, useApi } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader, Loading, Empty } from "../components/common";
import { Button } from "../components/ui/button";
import { fmtDateTime } from "../lib/format";
import { cn } from "../lib/utils";

const ICON = { scadenza: CalendarClock, turno: HardHat, assegnazione: ClipboardCheck, pagamento: Wallet, budget: TrendingUp };
const ICON_CLS = { scadenza: "bg-orange-500/15 text-orange-300", pagamento: "bg-emerald-500/15 text-emerald-300", budget: "bg-red-500/15 text-red-300" };

function NotificationItem({ n, onOpen }) {
  const Icon = ICON[n.type] || Bell;
  return (
    <li>
      <button onClick={() => onOpen(n)} className={cn("surface w-full p-4 text-left flex gap-3 hover:bg-accent/40 transition-colors", !n.read && "border-amber-500/50")} data-testid={`notification-${n.id}`}>
        <div className={cn("h-10 w-10 shrink-0 rounded-lg flex items-center justify-center", ICON_CLS[n.type] || "bg-sky-500/15 text-sky-300")}><Icon className="h-5 w-5" /></div>
        <div className="flex-1 min-w-0">
          <div className="flex justify-between gap-2"><span className="font-semibold text-sm">{n.title}</span><span className="text-[11px] text-muted-foreground shrink-0">{fmtDateTime(n.created_at)}</span></div>
          <div className="text-sm text-slate-300 mt-0.5">{n.message}</div>
        </div>
        {!n.read && <span className="h-2.5 w-2.5 rounded-full bg-amber-400 mt-1.5 shrink-0" />}
      </button>
    </li>
  );
}

function NotificationsBody({ data, loading, onOpen }) {
  if (loading && !data) return <Loading />;
  if (!data?.items?.length) return <div className="surface"><Empty title="Nessuna notifica" icon={Bell} /></div>;
  return (
    <ul className="space-y-2" data-testid="notifications-list">
      {data.items.map((n) => <NotificationItem key={n.id} n={n} onOpen={onOpen} />)}
    </ul>
  );
}

export default function Notifications() {
  const nav = useNavigate();
  const { setUnread } = useAuth();
  const { data, loading, reload } = useApi("/notifications");
  const open = async (n) => {
    if (!n.read) {
      await api.post(`/notifications/${n.id}/read`);
      setUnread((u) => Math.max(0, u - 1));
      reload();
    }
    if (n.link) nav(n.link);
  };
  const readAll = async () => {
    await api.post("/notifications/read-all");
    setUnread(0);
    reload();
  };
  return (
    <div className="max-w-3xl">
      <PageHeader eyebrow="Centro notifiche" title="Notifiche" subtitle={data ? `${data.unread} da leggere` : ""}
        actions={<Button variant="outline" onClick={readAll} disabled={!data?.unread} data-testid="notifications-read-all-btn"><CheckCheck className="h-4 w-4 mr-2" />Segna tutte come lette</Button>} />
      <NotificationsBody data={data} loading={loading} onOpen={open} />
    </div>
  );
}
